import {createContentEditableTextEditor} from '@frontapp/editor-html';
import {useEditor} from '@frontapp/editor-html/lib/react';
import React, {FC, useRef} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {globalWindow} from '../../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {useInitialEffect} from '../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {FrontKeyboardEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ContentEditableBox} from '../../../../common/boxedInputs/contentEditableBox';

/*
 * Props.
 */

interface ConversationHeaderSubjectEditorProps {
  initialSubject: string;
  onDoneEditing: (arg: string | null) => void;
  shouldSelectAll?: boolean;
  className?: string;
}

/*
 * Style.
 */

const StyledContentEditableWrapperDiv = styled.div`
  margin: -12px -8px;
`;

/*
 * Component.
 */

export const ConversationHeaderSubjectEditor: FC<ConversationHeaderSubjectEditorProps> = (props) => {
  const editor = useEditor(() => {
    const newEditor = createContentEditableTextEditor();

    newEditor.services.text.setText(props.initialSubject);

    return newEditor;
  }, []);
  const isCancelling = useRef(false);

  // Maybe select all the text if needed.
  useInitialEffect(() => {
    if (props.shouldSelectAll) {
      editor.services.selection.selectAll();
    }
  });

  const onEditorBlur = () => {
    // Hack to work around the fact that onKeyDown will always be called *after* onBlur
    globalWindow.setInstrumentedTimeout(() => {
      props.onDoneEditing(isCancelling.current ? null : editor.services.text.text);
      isCancelling.current = false;
    }, 0);
  };

  const onKeyDown = (ev: FrontKeyboardEvent) => {
    switch (ev.key) {
      case Key.Enter:
      case Key.Tab:
        ev.preventDefault();
        ev.stopPropagation();
        props.onDoneEditing(editor.services.text.text);
        break;

      case Key.Escape:
        isCancelling.current = true;
        break;

      default:
      // Do nothing.
    }
  };

  return (
    <StyledContentEditableWrapperDiv onKeyDown={onKeyDown}>
      <ContentEditableBox editor={editor} onBlur={onEditorBlur} shouldFocus className={props.className} />
    </StyledContentEditableWrapperDiv>
  );
};
